import { Tag, Typography } from 'antd';
import React from 'react';

import { useLinkedDocuments } from '@/hooks/useLinkedDocuments';
import { getTypeBadgeForDTO } from '@/models/knowledge-card-badge';

const { Text } = Typography;

/** Props for the `LinkedDocumentList` card-pill row. */
export interface LinkedDocumentListProps {
  cardIds: string[];
  clientId: string;
  projectId: string;
  accessToken: string;
  onOpen: (cardId: string) => void;
  /** Called when the user removes a card via the (X) on its pill. */
  onRemove?: (cardId: string) => void;
  /** The page context label used in the empty-state message, e.g. "assessment" or "weekly update". */
  context?: string;
}

/** Renders a row of colored pill tags for each linked knowledge card, with open and optional remove actions. */
export const LinkedDocumentList: React.FC<LinkedDocumentListProps> = ({
  cardIds,
  clientId,
  projectId,
  accessToken,
  onOpen,
  onRemove,
  context = 'assessment',
}) => {
  const { cards } = useLinkedDocuments(cardIds, accessToken, clientId, projectId);

  if (cards.length === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 13 }}>
        No documents linked yet. Upload a document above to link it to this {context}.
      </Text>
    );
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {cards.map(({ cardId, title, sourceType, sourceUrl }) => {
        const badge = getTypeBadgeForDTO(sourceType, sourceUrl);
        return (
          <Tag
            key={cardId}
            color={badge.color}
            style={{
              cursor: 'pointer',
              fontSize: 13,
              padding: '3px 10px',
              userSelect: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
            title={`Open: ${title}`}
            onClick={() => onOpen(cardId)}
          >
            {onRemove && (
              <button
                type="button"
                aria-label={`Remove ${title}`}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: 'inherit',
                  opacity: 0.65,
                  fontSize: 14,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(cardId);
                }}
              >
                ×
              </button>
            )}
            {title}
            <span style={{ fontWeight: 600, opacity: 0.75, fontSize: 11 }}>{badge.label}</span>
          </Tag>
        );
      })}
    </div>
  );
};

export default LinkedDocumentList;
